import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { tokens } from '../../theme/tokens';
import { ConfirmDialog, EmptyState, InlineMessage, ResponsiveRows, SectionCard, StatusChip } from '../ui';

/**
 * Unmapped products review (MOT-109), inside the SKU Crosswalk page (flag `sku_crosswalk`).
 * Lists Shopify listings the pricing audit could not link and sold lines with no catalog product,
 * suggests the catalog product by exact SKU / UPC, and writes only what the store confirms, as
 * crosswalk rows. The server does the matching (`/store/sku-crosswalk/unmapped`).
 */

export type UnmappedSource = 'shopify' | 'sold';

interface Suggestion {
  productId: string;
  name: string;
  brand?: string;
  sku?: string;
  upc?: string;
  matchedBy: 'sku' | 'upc';
}

interface Candidate {
  key: string;
  source: UnmappedSource;
  storeSku: string | null;
  storeBarcode: string | null;
  title: string | null;
  integrationId: string | null;
  unitsSold?: number;
  suggestion: Suggestion | null;
}

interface Page {
  candidates: Candidate[];
  scanned: number;
  alreadyMapped: number;
  total: number;
  nextCursor: string | null;
  integrationId: string | null;
}

interface Summary {
  shopifyUnmapped: number;
  shopifyIntegrationId: string | null;
  soldUnmatched: number;
}

interface AcceptResult {
  created: number;
  unchanged: number;
  failed: Array<{ storeSku: string | null; storeBarcode: string | null; error: string }>;
}

export interface MapManuallyRequest {
  storeSku: string;
  storeBarcode: string;
  integrationId: string;
}

interface Props {
  /** Initial source, from `?review=shopify|sold` on the page URL. */
  initialSource?: UnmappedSource | null;
  onMapManually: (request: MapManuallyRequest) => void;
}

type RowFilter = 'all' | 'suggested';

const SUMMARY_KEY = ['skuCrosswalkUnmappedSummary'];

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error || fallback;
};

const COLUMNS = [
  { key: 'select', header: '', width: '44px' },
  { key: 'item', header: 'item', width: 'minmax(0,1.5fr)' },
  { key: 'suggestion', header: 'suggestion', width: 'minmax(0,1.5fr)', mobileLabel: true },
  { key: 'actions', header: 'actions', width: '130px', align: 'end' as const },
];

export const UnmappedReviewCard: React.FC<Props> = ({ initialSource, onMapManually }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: summary } = useQuery<Summary>({
    queryKey: SUMMARY_KEY,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await makeApiCall('/store/sku-crosswalk/unmapped/summary', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.unmapped.loadError')));
      return response.json();
    },
  });

  const shopifyCount = summary?.shopifyUnmapped ?? 0;
  const soldCount = summary?.soldUnmatched ?? 0;
  const availableSources = useMemo<UnmappedSource[]>(
    () => [...(shopifyCount > 0 ? (['shopify'] as const) : []), ...(soldCount > 0 ? (['sold'] as const) : [])],
    [shopifyCount, soldCount],
  );

  const [pickedSource, setPickedSource] = useState<UnmappedSource | null>(initialSource ?? null);
  const source: UnmappedSource | null =
    pickedSource && availableSources.includes(pickedSource) ? pickedSource : availableSources[0] ?? null;

  const [rowFilter, setRowFilter] = useState<RowFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [failures, setFailures] = useState<AcceptResult['failed']>([]);

  const integrationId = summary?.shopifyIntegrationId ?? null;
  const listKey = ['skuCrosswalkUnmapped', source, integrationId];
  const list = useInfiniteQuery({
    queryKey: listKey,
    enabled: source !== null,
    initialPageParam: '' as string,
    getNextPageParam: (last: Page) => last.nextCursor || undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ source: source as string, limit: '50' });
      if (pageParam) params.set('cursor', pageParam);
      if (source === 'shopify' && integrationId) params.set('integrationId', integrationId);
      const response = await makeApiCall(`/store/sku-crosswalk/unmapped?${params}`, { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.unmapped.loadError')));
      return response.json() as Promise<Page>;
    },
  });

  const pages = list.data?.pages ?? [];
  const candidates = useMemo(() => pages.flatMap((p) => p.candidates), [pages]);
  const alreadyMapped = pages.reduce((sum, p) => sum + p.alreadyMapped, 0);
  const scanned = pages.reduce((sum, p) => sum + p.scanned, 0);
  const suggestedCount = candidates.filter((c) => c.suggestion).length;
  const visible = rowFilter === 'suggested' ? candidates.filter((c) => c.suggestion) : candidates;

  // A new source or a refreshed list starts with nothing selected.
  useEffect(() => {
    setSelected(new Set());
    setFailures([]);
    setRowFilter('all');
  }, [source]);

  const selectedCandidates = candidates.filter((c) => selected.has(c.key) && c.suggestion);
  const visibleSuggested = visible.filter((c) => c.suggestion);
  const allVisibleSelected = visibleSuggested.length > 0 && visibleSuggested.every((c) => selected.has(c.key));
  const someVisibleSelected = visibleSuggested.some((c) => selected.has(c.key));

  const toggleOne = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleSuggested.forEach((c) => next.delete(c.key));
      else visibleSuggested.forEach((c) => next.add(c.key));
      return next;
    });

  const acceptMutation = useMutation({
    mutationFn: async (rows: Candidate[]) => {
      const response = await makeApiCall('/store/sku-crosswalk/unmapped/accept', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          matches: rows.map((c) => ({
            productId: c.suggestion!.productId,
            storeSku: c.storeSku,
            storeBarcode: c.storeBarcode,
            integrationId: c.integrationId,
          })),
        }),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.unmapped.acceptError')));
      return response.json() as Promise<AcceptResult>;
    },
    onSuccess: (result) => {
      setConfirmOpen(false);
      setFailures(result.failed);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['skuCrosswalk'] });
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      queryClient.invalidateQueries({ queryKey: ['skuCrosswalkUnmapped'] });
      showToast(
        t('store.skuCrosswalk.unmapped.accepted', {
          count: result.created + result.unchanged,
          failed: result.failed.length,
        }),
        result.failed.length > 0 ? 'warning' : 'success',
      );
    },
    onError: (err: Error) => {
      setConfirmOpen(false);
      showToast(err.message, 'error');
    },
  });

  // Nothing is unmapped: the card has nothing to say.
  if (!summary || availableSources.length === 0 || source === null) return null;

  const columns = COLUMNS.map((c) => ({ ...c, header: c.header ? t(`store.skuCrosswalk.unmapped.columns.${c.header}`) : '' }));

  const renderCell = (c: Candidate, key: string): React.ReactNode => {
    switch (key) {
      case 'select':
        return (
          <Checkbox
            size="small"
            checked={selected.has(c.key)}
            disabled={!c.suggestion}
            onChange={() => toggleOne(c.key)}
            inputProps={{ 'aria-label': t('store.skuCrosswalk.unmapped.selectRow', { value: c.storeSku || c.storeBarcode || '' }) }}
          />
        );
      case 'item':
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {c.title || c.storeSku || c.storeBarcode}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink.faint, fontFamily: tokens.font.mono }} noWrap component="div">
              {[c.storeSku, c.storeBarcode].filter(Boolean).join(' · ')}
              {c.unitsSold != null ? ` · ${t('store.skuCrosswalk.unmapped.units', { count: c.unitsSold })}` : ''}
            </Typography>
          </Box>
        );
      case 'suggestion':
        return c.suggestion ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <StatusChip
              label={t(`store.skuCrosswalk.unmapped.matchedBy.${c.suggestion.matchedBy}`)}
              tone="info"
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{c.suggestion.name}</Typography>
              <Typography variant="caption" sx={{ color: tokens.ink.faint }} noWrap component="div">
                {[c.suggestion.brand, c.suggestion.sku].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">{t('store.skuCrosswalk.unmapped.noSuggestion')}</Typography>
        );
      default:
        return (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              onMapManually({
                storeSku: c.storeSku || '',
                storeBarcode: c.storeBarcode || '',
                integrationId: c.integrationId || '',
              })
            }
          >
            {t('store.skuCrosswalk.unmapped.mapManually')}
          </Button>
        );
    }
  };

  const coverage = [
    shopifyCount > 0 ? t('store.skuCrosswalk.unmapped.shopifyCoverage', { count: shopifyCount }) : '',
    soldCount > 0 ? t('store.skuCrosswalk.unmapped.soldCoverage', { count: soldCount }) : '',
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ mb: 2 }}>
      <SectionCard
        dense
        title={t('store.skuCrosswalk.unmapped.title')}
        actions={
          availableSources.length > 1 ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={source}
              onChange={(_e, next: UnmappedSource | null) => next && setPickedSource(next)}
              aria-label={t('store.skuCrosswalk.unmapped.sourceAria')}
            >
              <ToggleButton value="shopify">{t('store.skuCrosswalk.unmapped.sourceShopify', { count: shopifyCount })}</ToggleButton>
              <ToggleButton value="sold">{t('store.skuCrosswalk.unmapped.sourceSold', { count: soldCount })}</ToggleButton>
            </ToggleButtonGroup>
          ) : undefined
        }
      >
        <Box sx={{ px: '20px', py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{coverage}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t(source === 'shopify' ? 'store.skuCrosswalk.unmapped.helpShopify' : 'store.skuCrosswalk.unmapped.helpSold')}
          </Typography>
        </Box>

        {list.error && (
          <Box sx={{ px: '20px', pb: 1.5 }}>
            <InlineMessage severity="error">
              {(list.error as Error).message}{' '}
              <Button size="small" onClick={() => list.refetch()}>{t('store.skuCrosswalk.unmapped.retry')}</Button>
            </InlineMessage>
          </Box>
        )}

        {failures.length > 0 && (
          <Box sx={{ px: '20px', pb: 1.5 }}>
            <InlineMessage severity="warning">
              {t('store.skuCrosswalk.unmapped.failedIntro', { count: failures.length })}{' '}
              {failures.slice(0, 10).map((f) => `${f.storeSku || f.storeBarcode || '—'}: ${f.error}`).join(' | ')}
            </InlineMessage>
          </Box>
        )}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: '20px', py: 1, borderTop: `1px solid ${tokens.line.hairline}` }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rowFilter}
            onChange={(_e, next: RowFilter | null) => next && setRowFilter(next)}
            aria-label={t('store.skuCrosswalk.unmapped.filterAria')}
          >
            <ToggleButton value="all">{t('store.skuCrosswalk.unmapped.filterAll', { count: candidates.length })}</ToggleButton>
            <ToggleButton value="suggested">{t('store.skuCrosswalk.unmapped.filterSuggested', { count: suggestedCount })}</ToggleButton>
          </ToggleButtonGroup>
          {visibleSuggested.length > 0 && (
            <Stack direction="row" alignItems="center">
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                onChange={toggleAllVisible}
                inputProps={{ 'aria-label': t('store.skuCrosswalk.unmapped.selectAll') }}
              />
              <Typography variant="body2">{t('store.skuCrosswalk.unmapped.selectAll')}</Typography>
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          {selected.size > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{t('store.skuCrosswalk.unmapped.selected', { count: selected.size })}</Typography>
              <Button size="small" variant="contained" onClick={() => setConfirmOpen(true)}>
                {t('store.skuCrosswalk.unmapped.review')}
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())}>{t('store.skuCrosswalk.unmapped.clear')}</Button>
            </Stack>
          )}
        </Stack>

        <Box sx={{ maxHeight: 560, overflowY: 'auto' }}>
          <ResponsiveRows
            columns={columns}
            headerCellSx={{ textTransform: 'none', letterSpacing: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.body }}
            rows={visible}
            getRowKey={(c) => c.key}
            renderCell={renderCell}
            isLoading={list.isLoading}
            emptyState={
              <EmptyState
                size="compact"
                title={t(rowFilter === 'suggested' ? 'store.skuCrosswalk.unmapped.emptySuggested' : 'store.skuCrosswalk.unmapped.empty')}
                description={rowFilter === 'suggested' && list.hasNextPage ? t('store.skuCrosswalk.unmapped.emptySuggestedHint') : undefined}
              />
            }
          />
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: '20px', py: 1.5, borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {t('store.skuCrosswalk.unmapped.scanned', { scanned, mapped: alreadyMapped })}
          </Typography>
          {list.hasNextPage && (
            <Button size="small" variant="outlined" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
              {t('store.skuCrosswalk.unmapped.scanNext')}
            </Button>
          )}
        </Stack>
      </SectionCard>

      <ConfirmDialog
        open={confirmOpen}
        title={t('store.skuCrosswalk.unmapped.confirmTitle', { count: selectedCandidates.length })}
        message={t('store.skuCrosswalk.unmapped.confirmMessage')}
        preview={
          <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.subtle }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('store.skuCrosswalk.unmapped.confirmSummary', {
                count: selectedCandidates.length,
                sku: selectedCandidates.filter((c) => c.suggestion?.matchedBy === 'sku').length,
                upc: selectedCandidates.filter((c) => c.suggestion?.matchedBy === 'upc').length,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('store.skuCrosswalk.unmapped.confirmNote')}
            </Typography>
          </Box>
        }
        confirmLabel={t('store.skuCrosswalk.unmapped.confirmAction', { count: selectedCandidates.length })}
        loading={acceptMutation.isPending}
        onConfirm={() => acceptMutation.mutate(selectedCandidates)}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
};

export default UnmappedReviewCard;
